const COMPLEX_ANOMALY_RU = 'Комплексная аномалия';

/** RU/EN values from Qwen `problem_type` → i18n keys (farmer recommendations filter labels). */
const PROBLEM_TYPE_I18N_BY_TEXT: Record<string, string> = {
  'болезнь': 'FARMER_PROBLEM_TYPE_DISEASE',
  disease: 'FARMER_PROBLEM_TYPE_DISEASE',
  'вредитель': 'FARMER_PROBLEM_TYPE_PEST',
  pest: 'FARMER_PROBLEM_TYPE_PEST',
  'дефицит элементов': 'FARMER_PROBLEM_TYPE_ELEMENT_DEFICIENCY',
  дефицит: 'FARMER_PROBLEM_TYPE_ELEMENT_DEFICIENCY',
  'deficiency of elements': 'FARMER_PROBLEM_TYPE_ELEMENT_DEFICIENCY',
  deficiency: 'FARMER_PROBLEM_TYPE_ELEMENT_DEFICIENCY',
  'болезнь и вредитель': 'FARMER_PROBLEM_TYPE_DISEASE_AND_PEST',
  'disease and pest': 'FARMER_PROBLEM_TYPE_DISEASE_AND_PEST',
  [COMPLEX_ANOMALY_RU]: 'FARMER_PROBLEM_TYPE_COMPLEX',
  'не определено': 'FARMER_PROBLEM_TYPE_UNDEFINED',
  неопределено: 'FARMER_PROBLEM_TYPE_UNDEFINED',
  undefined: 'FARMER_PROBLEM_TYPE_UNDEFINED',
};

function reportRecord(row: unknown): Record<string, unknown> {
  return row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
}

function readReportString(row: unknown, field: 'anomalyType' | 'anomalyDescription'): string | null {
  const rec = reportRecord(row);
  const value = rec[field];
  if (typeof value === 'string') {
    const text = value.trim();
    return text || null;
  }
  return null;
}

/** Тип аномалии — `anomalyType` (problem_type с бэка). */
export function reportAnomalyType(row: unknown): string | null {
  return readReportString(row, 'anomalyType');
}

/** Описание — `anomalyDescription` (anomaly1 / anomaly2 с бэка). */
export function reportAnomalyDescription(row: unknown): string | null {
  return readReportString(row, 'anomalyDescription');
}

/** i18n-ключ для `anomalyType` (problem_type / агрегат с бэка). */
export function reportAnomalyTypeTranslateKey(row: unknown): string | null {
  const text = reportAnomalyType(row);
  if (!text) {
    return null;
  }
  const normalized = text.trim().toLowerCase();
  return PROBLEM_TYPE_I18N_BY_TEXT[normalized] ?? PROBLEM_TYPE_I18N_BY_TEXT[text.trim()] ?? null;
}

export interface ReportQwenAnomalyLine {
  typeLabel: string;
  description: string;
}

/** Keep rows with anomaly_presence true; omit explicit false. Missing field → include. */
function passesAnomalyPresenceFilter(value: unknown): boolean {
  if (value === false || value === 0 || value === '0') {
    return false;
  }
  if (typeof value === 'string') {
    const n = value.trim().toLowerCase();
    if (n === 'false' || n === 'no' || n === '0') {
      return false;
    }
  }
  return true;
}

function resolveTypeLabel(raw: unknown): string {
  if (raw == null || raw === '') {
    return '';
  }
  const text = String(raw).trim();
  const normalized = text.toLowerCase();
  const key = PROBLEM_TYPE_I18N_BY_TEXT[normalized] ?? PROBLEM_TYPE_I18N_BY_TEXT[text];
  if (key) {
    return key;
  }
  return text;
}

/** Text lines from `anomaly1`, `anomaly2`, … on a qwen item. */
function extractAnomalyTextsFromEntry(entry: Record<string, unknown>): string[] {
  const texts: string[] = [];
  for (let i = 1; i <= 20; i++) {
    const val = entry[`anomaly${i}`] ?? entry[`Anomaly${i}`];
    if (val != null && String(val).trim()) {
      texts.push(String(val).trim());
    }
  }
  if (!texts.length) {
    const fallback = entry['anomaly_description'] ?? entry['anomalyDescription'] ?? entry['description'];
    if (fallback != null && String(fallback).trim()) {
      texts.push(String(fallback).trim());
    }
  }
  return texts;
}

function resolveQwenResultItems(rec: Record<string, unknown>): unknown[] {
  const qwen = rec['qwenJobResultResponse'] ?? rec['QwenJobResultResponse'];
  if (qwen != null && typeof qwen === 'object' && !Array.isArray(qwen)) {
    const items = (qwen as Record<string, unknown>)['items'] ?? (qwen as Record<string, unknown>)['Items'];
    if (Array.isArray(items)) {
      return items;
    }
  }
  if (Array.isArray(qwen)) {
    return qwen;
  }
  const responses = rec['qwenJobResultResponses'];
  return Array.isArray(responses) ? responses : [];
}

/** Rows from `qwenJobResultResponse.items` (`anomaly1`, …) with optional `anomaly_presence` filter. */
export function extractReportQwenAnomalyLines(row: unknown): ReportQwenAnomalyLine[] {
  const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const rawList = resolveQwenResultItems(rec);

  const lines: ReportQwenAnomalyLine[] = [];
  for (const item of rawList) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const entry = item as Record<string, unknown>;
    const presence = entry['anomaly_presence'] ?? entry['anomalyPresence'];
    if (!passesAnomalyPresenceFilter(presence)) {
      continue;
    }
    const typeRaw = entry['problem_type'] ?? entry['problemType'] ?? entry['anomalyType'] ?? entry['type'];
    const typeLabel = resolveTypeLabel(typeRaw);
    const descriptions = extractAnomalyTextsFromEntry(entry);
    if (!descriptions.length) {
      if (!typeLabel) {
        continue;
      }
      lines.push({typeLabel, description: ''});
      continue;
    }
    for (const description of descriptions) {
      if (!typeLabel && !description) {
        continue;
      }
      lines.push({typeLabel, description});
    }
  }
  return lines;
}

export function reportFieldId(row: unknown): string | null {
  const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const fields = rec['fields'] as Record<string, unknown> | undefined;
  const id = rec['fieldId'] ?? fields?.['id'] ?? fields?.['fieldId'];
  return id != null && String(id).trim() ? String(id) : null;
}

export function reportFieldName(row: unknown): string {
  const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const fields = rec['fields'] as Record<string, unknown> | undefined;
  const name = fields?.['name'] ?? rec['fieldName'];
  return name != null && String(name).trim() ? String(name).trim() : '—';
}

export function reportRegionId(row: unknown): string | null {
  const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const fields = rec['fields'] as Record<string, unknown> | undefined;
  const id = fields?.['regionId'] ?? rec['regionId'];
  return id != null && String(id).trim() ? String(id) : null;
}

export function reportSeasonCropId(row: unknown): string | null {
  const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const seasons = rec['seasons'] as Record<string, unknown> | undefined;
  const id = seasons?.['cropId'] ?? rec['cropId'] ?? rec['seasonCropId'];
  return id != null && String(id).trim() ? String(id) : null;
}

export function reportRowId(row: unknown): string | null {
  const rec = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const id = rec['id'];
  return id != null && String(id).trim() ? String(id) : null;
}
